import { useState, useMemo } from 'react'
import {
  Search,
  AlertCircle,
  MessageCircle,
  Mail,
  ExternalLink,
  CheckCircle,
  Clock,
  AlertTriangle,
  Twitter,
  Facebook,
  Linkedin,
  Globe,
  Smartphone,
} from 'lucide-react'
import { useAccountsStore } from '../store/accountsStore'

// Mock data for social media messages
const mockSocialMessages = [
  {
    id: 'TK-001',
    platform: 'tiktok',
    handle: '@cybertection',
    fromName: 'Fan User 1',
    message: 'Love your content! Keep it up 🔥',
    timestamp: new Date(Date.now() - 2 * 60000),
    status: 'ANSWERED',
    importance: 'low',
    aiResponse: 'Thanks so much for the support! 🙌 Really appreciate it!',
    type: 'comment',
  },
  {
    id: 'IG-001',
    platform: 'instagram',
    handle: '@cyberjoe2050',
    fromName: 'Security Question DM',
    message: 'How do I secure my WiFi network?',
    timestamp: new Date(Date.now() - 5 * 60000),
    status: 'ANSWERED',
    importance: 'low',
    aiResponse: 'Great question! Here are the key steps: 1) Change default password, 2) Use WPA3 encryption, 3) Disable WPS, 4) Update router firmware regularly.',
    type: 'dm',
  },
  {
    id: 'X-001',
    platform: 'x',
    handle: '@Cybertection',
    fromName: 'DevCorp CEO',
    message: 'Interested in partnership opportunities. Can we schedule a call?',
    timestamp: new Date(Date.now() - 15 * 60000),
    status: 'UNOPENED',
    importance: 'critical',
    aiResponse: null,
    type: 'reply',
  },
  {
    id: 'YT-001',
    platform: 'youtube',
    handle: '@Cyberjoe2050',
    fromName: 'Learning User',
    message: 'Can you make a tutorial on blockchain security?',
    timestamp: new Date(Date.now() - 20 * 60000),
    status: 'ANSWERED',
    importance: 'low',
    aiResponse: 'That\'s a great topic! We\'re already planning content on blockchain security. Subscribe to stay updated! 📺',
    type: 'comment',
  },
  {
    id: 'FB-001',
    platform: 'facebook',
    handle: 'cybertectionLLC',
    fromName: 'Friend Request Message',
    message: 'Hey, long time! How are you doing?',
    timestamp: new Date(Date.now() - 30 * 60000),
    status: 'ANSWERED',
    importance: 'low',
    aiResponse: 'Hey! Doing great, thanks for reaching out! 😊',
    type: 'message',
  },
  {
    id: 'EMAIL-001',
    platform: 'email',
    handle: 'joeburnz25@gmail.com',
    fromName: 'TechVentures LLC',
    message: 'Partnership proposal: Integration opportunity',
    timestamp: new Date(Date.now() - 45 * 60000),
    status: 'UNOPENED',
    importance: 'critical',
    aiResponse: null,
    type: 'email',
  },
  {
    id: 'DISCORD-001',
    platform: 'discord',
    handle: 'Cybertection Server',
    fromName: 'Community Member',
    message: 'How do I get started with Python security?',
    timestamp: new Date(Date.now() - 60 * 60000),
    status: 'ANSWERED',
    importance: 'low',
    aiResponse: 'Check out our getting started guide in #resources! Happy to help. 🐍',
    type: 'dm',
  },
  {
    id: 'LINKEDIN-001',
    platform: 'linkedin',
    handle: '/josephplaugher',
    fromName: 'Enterprise CTO',
    message: 'We\'d like to explore your security solution for enterprise use',
    timestamp: new Date(Date.now() - 90 * 60000),
    status: 'UNOPENED',
    importance: 'critical',
    aiResponse: null,
    type: 'message',
  },
  {
    id: 'EMAIL-002',
    platform: 'email',
    handle: 'cybertection@yahoo.com',
    fromName: 'Course Student',
    message: 'Finished Module 3! When is Module 4 coming out?',
    timestamp: new Date(Date.now() - 120 * 60000),
    status: 'ANSWERED',
    importance: 'low',
    aiResponse: 'Awesome! Module 4 launches next week. Thanks for your patience! 🎓',
    type: 'email',
  },
  {
    id: 'GITHUB-001',
    platform: 'github',
    handle: 'cybertection',
    fromName: 'Open Source Contributor',
    message: 'Question about the authentication module in your repo',
    timestamp: new Date(Date.now() - 150 * 60000),
    status: 'ANSWERED',
    importance: 'medium',
    aiResponse: 'Great question! Check our documentation at /docs/auth for detailed info.',
    type: 'issue',
  },
]

// Platform info
const platformInfo = {
  tiktok: { label: 'TikTok', color: 'bg-black text-white', icon: '🎵' },
  instagram: { label: 'Instagram', color: 'bg-pink-500 text-white', icon: '📸' },
  x: { label: 'X (Twitter)', color: 'bg-gray-900 text-white', icon: '𝕏' },
  youtube: { label: 'YouTube', color: 'bg-red-600 text-white', icon: '▶️' },
  facebook: { label: 'Facebook', color: 'bg-blue-600 text-white', icon: '👍' },
  email: { label: 'Email', color: 'bg-blue-500 text-white', icon: '✉️' },
  discord: { label: 'Discord', color: 'bg-indigo-600 text-white', icon: '💬' },
  linkedin: { label: 'LinkedIn', color: 'bg-blue-700 text-white', icon: '👔' },
  github: { label: 'GitHub', color: 'bg-gray-800 text-white', icon: '🐙' },
}

const importanceColors = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
}

const statusIcons = {
  ANSWERED: <CheckCircle className="text-green-600" size={20} />,
  UNOPENED: <AlertCircle className="text-red-600" size={20} />,
  PENDING: <Clock className="text-yellow-600" size={20} />,
}

export default function SocialMediaManagerPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [filterPlatform, setFilterPlatform] = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [selectedMessage, setSelectedMessage] = useState(null)

  // Get social media accounts from store
  const allAccounts = useAccountsStore((state) => state.accounts)
  const socialMediaAccounts = useMemo(
    () => allAccounts.filter((account) => account.type === 'social_media'),
    [allAccounts]
  )

  const getServiceIcon = (service) => {
    const iconMap = {
      'Twitter': <Twitter className="w-5 h-5 text-blue-400" />,
      'Facebook': <Facebook className="w-5 h-5 text-blue-600" />,
      'LinkedIn': <Linkedin className="w-5 h-5 text-blue-700" />,
      'Instagram': <Smartphone className="w-5 h-5 text-pink-400" />,
      'TikTok': <Globe className="w-5 h-5 text-black dark:text-white" />,
      'Discord': <Globe className="w-5 h-5 text-indigo-500" />,
    }
    return iconMap[service] || <Globe className="w-5 h-5 text-gray-400" />
  }

  // Calculate stats
  const stats = useMemo(() => {
    return {
      total: mockSocialMessages.length,
      answered: mockSocialMessages.filter(m => m.status === 'ANSWERED').length,
      unopened: mockSocialMessages.filter(m => m.status === 'UNOPENED').length,
      critical: mockSocialMessages.filter(m => m.importance === 'critical').length,
    }
  }, [])

  // Filter messages
  const filteredMessages = useMemo(() => {
    let filtered = mockSocialMessages.filter(msg => {
      const matchesSearch =
        msg.fromName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        msg.message.toLowerCase().includes(searchQuery.toLowerCase()) ||
        msg.platform.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesPlatform = filterPlatform === 'all' || msg.platform === filterPlatform
      const matchesStatus = filterStatus === 'all' || msg.status === filterStatus

      return matchesSearch && matchesPlatform && matchesStatus
    })

    // Sort by importance then timestamp
    const importanceOrder = { critical: 0, high: 1, medium: 2, low: 3 }
    return filtered.sort((a, b) => {
      const importanceDiff = importanceOrder[a.importance] - importanceOrder[b.importance]
      if (importanceDiff !== 0) return importanceDiff
      return b.timestamp - a.timestamp
    })
  }, [searchQuery, filterPlatform, filterStatus])

  const selectedMsg = selectedMessage ? mockSocialMessages.find(m => m.id === selectedMessage) : null

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Social Media Manager</h1>
        <p className="text-blue-200 mt-1">Auto-answer messages and monitor all your social channels</p>
      </div>

      {/* Connected Accounts */}
      {socialMediaAccounts.length > 0 && (
        <div className="bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-lg border border-blue-500/30 p-4">
          <h3 className="text-sm font-semibold text-blue-300 mb-3">Connected Accounts</h3>
          <div className="flex flex-wrap gap-2">
            {socialMediaAccounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center space-x-2 bg-slate-700/50 rounded-lg px-3 py-2 border border-blue-500/20"
              >
                {getServiceIcon(account.service)}
                <div>
                  <p className="text-sm font-semibold text-white">{account.service}</p>
                  <p className="text-xs text-blue-300">{account.username}</p>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded ${
                  account.status === 'Connected' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
                }`}>
                  {account.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {socialMediaAccounts.length === 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="text-yellow-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-yellow-300">No Social Media Accounts Connected</h3>
            <p className="text-sm text-yellow-200 mt-1">
              Go to <a href="/accounts" className="underline font-semibold hover:text-yellow-100">Accounts & Integrations</a> to add your social media accounts.
            </p>
          </div>
        </div>
      )}

      {/* Alert Banner */}
      {stats.unopened > 0 && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-start space-x-3">
          <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-red-300">Important Messages Require Review</h3>
            <p className="text-sm text-red-200 mt-1">
              You have {stats.unopened} important message{stats.unopened !== 1 ? 's' : ''} from partnerships, business
              inquiries, and key contacts that need your personal attention.
            </p>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-4">
          <p className="text-sm text-blue-200 font-medium">Total Messages</p>
          <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-4">
          <p className="text-sm text-blue-200 font-medium">Auto-Answered</p>
          <p className="text-2xl font-bold text-green-400 mt-1">{stats.answered}</p>
        </div>
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-4">
          <p className="text-sm text-blue-200 font-medium">Awaiting Review</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{stats.unopened}</p>
        </div>
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/30 p-4">
          <p className="text-sm text-blue-200 font-medium">Critical Messages</p>
          <p className="text-2xl font-bold text-orange-400 mt-1">{stats.critical}</p>
        </div>
      </div>

      {/* Main Content - Two Column */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Message List */}
        <div className="lg:col-span-2 space-y-4">
          {/* Filters */}
          <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search messages..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-blue-500/30 rounded-lg bg-slate-700/50 text-white placeholder-blue-300/50 focus:outline-none focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/40"
                  />
                </div>
              </div>

              {/* Platform Filter */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Platform</label>
                <select
                  value={filterPlatform}
                  onChange={(e) => setFilterPlatform(e.target.value)}
                  className="w-full px-4 py-2 border border-blue-500/30 rounded-lg bg-slate-700/50 text-white focus:outline-none focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/40"
                >
                  <option value="all">All Platforms</option>
                  <option value="email">Email</option>
                  <option value="instagram">Instagram</option>
                  <option value="tiktok">TikTok</option>
                  <option value="x">X (Twitter)</option>
                  <option value="youtube">YouTube</option>
                  <option value="facebook">Facebook</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="discord">Discord</option>
                  <option value="github">GitHub</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-blue-500/30 rounded-lg bg-slate-700/50 text-white focus:outline-none focus:border-blue-500/60 focus:ring-2 focus:ring-blue-500/40"
                >
                  <option value="all">All Status</option>
                  <option value="ANSWERED">Auto-Answered</option>
                  <option value="UNOPENED">Awaiting Review</option>
                  <option value="PENDING">Pending</option>
                </select>
              </div>
            </div>
          </div>

          {/* Message List */}
          <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 overflow-hidden">
            {filteredMessages.length > 0 ? (
              <div className="divide-y divide-blue-500/20">
                {filteredMessages.map((msg) => (
                  <button
                    key={msg.id}
                    onClick={() => setSelectedMessage(msg.id)}
                    className={`w-full text-left p-4 hover:bg-slate-700/50 transition-colors border-l-4 ${
                      selectedMessage === msg.id
                        ? 'border-l-blue-500 bg-slate-700/70'
                        : 'border-l-transparent hover:border-l-blue-500/50'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Status Icon */}
                      <div className="flex-shrink-0 mt-0.5">{statusIcons[msg.status]}</div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-1">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${platformInfo[msg.platform].color}`}>
                            {platformInfo[msg.platform].icon} {platformInfo[msg.platform].label}
                          </span>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${importanceColors[msg.importance]}`}
                          >
                            {msg.importance.toUpperCase()}
                          </span>
                        </div>
                        <h3 className="font-semibold text-white truncate">{msg.fromName}</h3>
                        <p className="text-sm text-blue-200 truncate mt-1">{msg.message}</p>
                        <p className="text-xs text-blue-300/70 mt-2">{msg.timestamp.toLocaleString()}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <p className="text-blue-300/70">No messages found</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: Message Detail Panel */}
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6 h-fit sticky top-24">
          {selectedMsg ? (
            <div className="space-y-4">
              <div className="pb-4 border-b border-blue-500/30">
                <div className="flex items-center space-x-2 mb-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${platformInfo[selectedMsg.platform].color}`}>
                    {platformInfo[selectedMsg.platform].icon} {platformInfo[selectedMsg.platform].label}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${importanceColors[selectedMsg.importance]}`}>
                    {selectedMsg.importance.toUpperCase()}
                  </span>
                </div>
                <h3 className="text-lg font-bold text-white">{selectedMsg.fromName}</h3>
                <p className="text-sm text-blue-300 mt-1">{selectedMsg.handle}</p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-blue-200 mb-2 uppercase">Message</label>
                <div className="bg-slate-700/50 rounded p-3 text-sm text-white break-words border border-blue-500/20">
                  {selectedMsg.message}
                </div>
              </div>

              {selectedMsg.status === 'ANSWERED' && selectedMsg.aiResponse && (
                <div>
                  <label className="block text-xs font-semibold text-blue-200 mb-2 uppercase">AI Response Sent</label>
                  <div className="bg-green-500/20 border border-green-500/50 rounded p-3 text-sm text-green-200 break-words">
                    {selectedMsg.aiResponse}
                  </div>
                </div>
              )}

              {selectedMsg.status === 'UNOPENED' && (
                <div className="bg-red-500/20 border border-red-500/50 rounded p-3">
                  <p className="text-sm text-red-300 font-medium">⚠️ Awaiting Your Review</p>
                  <p className="text-xs text-red-200 mt-1">
                    This message is from an important contact and requires your personal attention.
                  </p>
                </div>
              )}

              <div className="pt-4 border-t border-blue-500/30 space-y-2">
                <p className="text-xs text-blue-300">
                  <strong>Time:</strong> {selectedMsg.timestamp.toLocaleString()}
                </p>
                <p className="text-xs text-blue-300">
                  <strong>Type:</strong> {selectedMsg.type.charAt(0).toUpperCase() + selectedMsg.type.slice(1)}
                </p>
                {selectedMsg.status === 'UNOPENED' && (
                  <button className="w-full mt-4 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors text-sm">
                    Mark as Reviewed & Send Response
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-blue-300/70">
              <MessageCircle size={40} className="mx-auto mb-3 text-blue-400/50" />
              <p>Select a message to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Auto-Answer Activity Log */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <h3 className="font-bold text-white mb-4">Recent Auto-Answer Activity</h3>
        <div className="space-y-3">
          {mockSocialMessages
            .filter(m => m.status === 'ANSWERED')
            .sort((a, b) => b.timestamp - a.timestamp)
            .slice(0, 5)
            .map((msg) => (
              <div key={msg.id} className="flex items-center space-x-3 text-sm">
                <CheckCircle className="text-green-400 flex-shrink-0" size={16} />
                <span className="text-white flex-1">
                  {msg.fromName} on <strong>{platformInfo[msg.platform].label}</strong>
                </span>
                <span className="text-blue-300/70 text-xs">{msg.timestamp.toLocaleTimeString()}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
